/**
 * tzn_progress-bar.js
 * ---------------------------------------------------------------------------
 * Custom element <cart-progress-bar>, powers the 3-segment gamification
 * progress bar rendered by snippets/free-shipping-bar.liquid (cart page +
 * cart drawer).
 *
 * Stages (min, max, unreached text, reached text) are configured in
 * Theme settings > Cart > Progress bar stages, and passed down server-side
 * as a JSON blob in the `data-stages` attribute. The track is always split
 * into 3 equal segments (see CSS); each segment fills independently between
 * its own min/max.
 *
 * This element reacts to the `cart:change` event (dispatched by the theme
 * after every AJAX cart update) and updates, on the *existing* DOM nodes
 * (no re-render) so the CSS transition on each segment fill keeps animating:
 *  - the message text (with the %remaining% placeholder replaced)
 *  - each segment's fill (--progress custom property, 0-1)
 *  - the "is-reached" class per segment + the bar's overall "active" class
 *
 * Kept out of theme.js.liquid on purpose: that file is compiled/generated
 * by the Impact base theme and must not be hand-edited. Written as a plain
 * ES6 class (not transpiled) because native custom elements require calling
 * `super()` through a real class constructor to upgrade correctly.
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    var format =
      (window.themeVariables &&
        window.themeVariables.settings &&
        window.themeVariables.settings.moneyFormat) ||
      '{{amount}}';

    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    var placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision === undefined ? 2 : precision;
      thousands = thousands === undefined ? '.' : thousands;
      decimal = decimal === undefined ? ',' : decimal;

      if (isNaN(number) || number === null) return 0;

      number = (number / 100).toFixed(precision);

      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';

      return dollars + centsPart;
    }

    var value;
    switch (placeholder) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = withDelimiters(cents, 2);
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function currencyRate() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
  }

  class CartProgressBar extends HTMLElement {
    constructor() {
      super();
      this._onCartChanged = this._onCartChanged.bind(this);
    }

    connectedCallback() {
      document.addEventListener('cart:change', this._onCartChanged);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCartChanged);
    }

    _getStages() {
      try {
        const stages = JSON.parse(this.dataset.stages || '[]');
        const rate = currencyRate();

        return stages.map((stage) => ({
          min: Math.round(stage.min * rate),
          max: Math.round(stage.max * rate),
          unreached: stage.unreached,
          reached: stage.reached,
        }));
      } catch (e) {
        return [];
      }
    }

    _onCartChanged(event) {
      const cart = (event.detail && event.detail.cart) || {};
      const items = cart.items || [];
      const discounts = cart.cart_level_discount_applications || [];

      // Use final_line_price (already reflects per-product/quantity-break discounts - those aren't
      // caused by OUR thresholds, so the real, payable amount must be used for them), then add back
      // cart-level automatic discounts: those only exist BECAUSE the order total crossed one of our
      // own stage thresholds, so without adding them back a stage could never visually "lock in"
      // (reaching it triggers the discount, which drops the total back below it). Same reasoning as
      // the server-side render in snippets/free-shipping-bar.liquid.
      const priceForItems = items
        .filter((item) => item.requires_shipping)
        .reduce((sum, item) => sum + item.final_line_price, 0);

      const cartDiscount = discounts.reduce((sum, discount) => sum + discount.total_allocated_amount, 0);

      this._update(priceForItems + cartDiscount);
    }

    _update(totalPrice) {
      const stages = this._getStages();
      if (!stages.length) return;

      const segments = this.querySelectorAll('.progress-bar-segment');
      const messageElement = this.querySelector('[data-progress-message]');

      let currentStage = null;

      stages.forEach((stage, index) => {
        const segment = segments[index];
        if (!segment) return;

        const fill = segment.querySelector('.progress-bar-segment__fill');
        let ratio = 0;

        if (totalPrice >= stage.max) {
          ratio = 1;
        } else if (totalPrice > stage.min) {
          ratio = (totalPrice - stage.min) / (stage.max - stage.min);
        }

        if (fill) fill.style.setProperty('--progress', ratio);
        segment.classList.toggle('is-reached', ratio >= 1);

        if (!currentStage && totalPrice < stage.max) {
          currentStage = stage;
        }
      });

      if (currentStage) {
        const remaining = currentStage.max - totalPrice;
        const remainingHtml = '<span class="bold text-accent">' + formatMoney(remaining) + '</span>';

        if (messageElement) {
          messageElement.innerHTML = currentStage.unreached.replace('%remaining%', remainingHtml);
        }
        this.classList.remove('active');
      } else {
        const lastStage = stages[stages.length - 1];

        if (messageElement) {
          messageElement.innerHTML = lastStage.reached;
        }
        this.classList.add('active');
      }
    }
  }

  if (!window.customElements.get('cart-progress-bar')) {
    window.customElements.define('cart-progress-bar', CartProgressBar);
  }
})();
